import { useEffect, useState } from "react";
import { keyframes } from "@emotion/react";
import { Button, styled } from "@mui/material";

import { downloadReport, isClinic, transformClasses } from "..";

import {
  IDoctorConsultationSelection,
  IProfileDropdownEnums,
  IKeyPairsValues,
  IDocumentTypeEnum,
  IPatientDetailEnumWeb,
} from "@/models/doctor";

import style from "./index.module.css";

import GetImages from "@/components/GetImages";

import { useAppDispatch } from "@/store/hooks";
import {
  setIsVideoChatMobileShown,
  showHideAddMember,
} from "@/store/slices/videoAppointment";

import { IOrderTypeEnums } from "@/models/ApiRequestResponse/patient";
import {
  IOrderStatusEnum,
  IProfileAnalyticsEnum,
} from "@/models/ApiRequestResponse/pharmacy";
import { IRoleType } from "@/models/role";
import { IPaymentMethod } from "@/models/ApiRequestResponse/doctor";

import { socialPlatformLinks } from "@/utils/Config";

export const getStartedTutorial = [
  {
    image: <GetImages name="StayConnectedImage" width="304" height="245" />,
    title: "stayConnectedTitle",
    description: "stayConnectedDescription",
  },
  {
    image: <GetImages name="TestOnlineImage" width="268" height="279" />,
    title: "bookTestTitle",
    description: "bookTestDescription",
  },
  {
    image: <GetImages name="BookDoctorImage" width="310" height="199" />,
    title: "bookDoctorTitle",
    description: "bookDoctorDescription",
  },
];

export const locationAccessDetail = [
  {
    icon: <GetImages name="DoctorSearchIcon" width="20" height="20" />,
    title: "doctors",
    subHeding: "nearbyText",
  },
  {
    icon: <GetImages name="ClinicIcon" width="20" height="20" />,
    title: "clinicsText",
    subHeding: "nearbyText",
  },
  {
    icon: <GetImages name="LocationAccessIcon" width="36" height="36" />,
    title: "direction",
    subHeding: "toThemText",
  },
];

export const appointmentActionList = [
  {
    identifier: IPatientDetailEnumWeb.CANCEL,
    label: "Cancel",
    icon: "CancelIcon",
    labelStyle: style.actionText,
    backStyle: style.actionEventTriggerContainer,
  },
  {
    identifier: IPatientDetailEnumWeb.RESCHEDULE,
    label: "Reschedule",
    icon: "RescheduleIcon",
    labelStyle: style.actionText,
    backStyle: style.actionEventTriggerContainer,
  },
  {
    identifier: IPatientDetailEnumWeb.CHAT,
    label: "Chat",
    icon: "ChatIcon",
    labelStyle: style.actionText,
    backStyle: style.actionEventTriggerContainer,
  },
  {
    identifier: IPatientDetailEnumWeb.RECORD,
    label: "Record",
    icon: "RecordWebIcon",
    labelStyle: style.actionText,
    backStyle: style.actionEventTriggerContainer,
  },
  {
    identifier: IPatientDetailEnumWeb.PRESCRIPTION,
    label: "Prescription",
    icon: "PrescriptionWebIcon",
    labelStyle: style.actionText,
    backStyle: style.actionEventTriggerContainer,
  },
];

export const ipDashboardActionList = [
  {
    identifier: IPatientDetailEnumWeb.CLINICAL_TEAM,
    label: "Clinical Team",
    icon: "searchDoctor",
    labelStyle: style.actionText,
    backStyle: style.actionEventTriggerContainer,
  },
  {
    identifier: IPatientDetailEnumWeb.CHAT,
    label: "Chat",
    icon: "ChatIcon",
    labelStyle: style.actionText,
    backStyle: style.actionEventTriggerContainer,
  },
  {
    identifier: IPatientDetailEnumWeb.IP_DETAIL,
    label: "Details",
    icon: "AdmitDetailIcon",
    labelStyle: style.actionText,
    backStyle: style.actionEventTriggerContainer,
  },
  {
    identifier: IPatientDetailEnumWeb.RECORD,
    label: "Record",
    icon: "RecordWebIcon",
    labelStyle: style.actionText,
    backStyle: style.actionEventTriggerContainer,
  },
];

export const videoToolbarTop = () => {
  const [cameraDevice, setCameraDevice] = useState<"user" | "environment">(
    "user"
  );
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [availableDevices, setAvailableDevices] = useState<MediaDeviceInfo[]>(
    []
  );
  const dispatch = useAppDispatch();

  // Get available camera devices
  useEffect(() => {
    const getDevices = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(
          (device) => device.kind === "videoinput"
        );
        setAvailableDevices(videoDevices);
      } catch (error) {
        console.error("Error getting devices:", error);
      }
    };
    getDevices();
  }, []);

  const switchCamera = async () => {
    if (availableDevices.length <= 1) {
      // If only one camera is available, toggle between front and back
      setCameraDevice((prev) => (prev === "user" ? "environment" : "user"));
    } else {
      // If multiple cameras are available, switch to the next one
      const currentIndex = availableDevices.findIndex((device) =>
        device.label
          .toLowerCase()
          .includes(cameraDevice === "user" ? "front" : "back")
      );
      const nextIndex = (currentIndex + 1) % availableDevices.length;
      const nextDevice = availableDevices[nextIndex];
      setCameraDevice(
        nextDevice.label.toLowerCase().includes("front")
          ? "user"
          : "environment"
      );
    }
  };

  useEffect(() => {
    const getStream = async () => {
      if (stream) {
        // Stop previous tracks
        stream.getTracks().forEach((track) => track.stop());
      }

      try {
        const constraints: MediaStreamConstraints = {
          video: {
            facingMode: cameraDevice,
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: true,
        };

        // For iOS devices, we need to handle camera switching differently
        if (
          /iPad|iPhone|iPod/.test(navigator.userAgent) &&
          !(window as any).MSStream
        ) {
          // On iOS, we need to ensure we're using the correct camera
          if (availableDevices.length > 1) {
            const targetDevice = availableDevices.find((device) =>
              device.label
                .toLowerCase()
                .includes(cameraDevice === "user" ? "front" : "back")
            );
            if (targetDevice) {
              (constraints.video as MediaTrackConstraints).deviceId = {
                exact: targetDevice.deviceId,
              };
            }
          }
        }

        const newStream = await navigator.mediaDevices.getUserMedia(
          constraints
        );
        setStream(newStream);
      } catch (err) {
        console.error("Failed to get media stream:", err);
        // If switching fails, try to revert to the previous camera
        setCameraDevice((prev) => (prev === "user" ? "environment" : "user"));
      }
    };

    getStream();
  }, [cameraDevice, availableDevices]);

  const videoToolbarTopList = [
    {
      icon: "VideoChatIcon",
      css: style.btnLayer,
      handler: () => dispatch(setIsVideoChatMobileShown(true)),
    },
    {
      icon: "AddMemberIcon",
      css: transformClasses(style.btnLayer, style.spacing),
      handler: () => dispatch(showHideAddMember()),
    },
    {
      icon: "CameraSwitchIcon",
      css: style.btnLayer,
      handler: () => switchCamera(),
    },
  ];

  return { videoToolbarTopList };
};

export const socialLinks = [
  {
    iconName: "InstagramIcon",
    link: socialPlatformLinks?.instagram,
  },
  {
    iconName: "FBIcon",
    link: socialPlatformLinks?.facebook,
  },
  {
    iconName: "YoutubeIcon",
    link: socialPlatformLinks?.youtube,
  },
  {
    iconName: "LinkedInIcon",
    link: socialPlatformLinks?.linkedin,
  },
  {
    iconName: "TwitterIcon",
    link: socialPlatformLinks?.twitter,
  },
];

export const contactBtns = (token: string | null) => [
  {
    preText: null,
    btnText: "Call Us",
    iconName: "TelePhoneIcon",
    link: "tel:+919876543210",
    type: "tel",
  },
  {
    preText: null,
    btnText: "FAQ",
    iconName: "BookPlusIcon",
    link: "/faq",
    type: "route",
  },
  ...(token
    ? []
    : [
        {
          preText: "Already have a Account ?",
          btnText: "Log in",
          iconName: null,
          link: "/login",
          type: "route",
        },
      ]),
];

export const homeTitleDetail = [
  {
    icon: <GetImages name="DoctorSearchIcon" width="20" height="20" />,
    title: "doctors",
    subHeding: "available",
    moveTo: "/find-doctor",
  },
  {
    icon: <GetImages name="MedicineSmallIcon" width="20" height="20" />,
    title: "medicines",
    subHeding: "free_delivery",
    moveTo: "/medicines",
  },
  {
    icon: <GetImages name="TestSmallIcon" width="20" height="20" />,
    title: "tests",
    subHeding: "trusted_lab_near_you",
    moveTo: "/test-reports",
  },
];

export const medicalRecordFilterList = [
  {
    label: "All",
    value: IDocumentTypeEnum.All,
  },
  {
    label: "Prescriptions",
    value: IDocumentTypeEnum.PRESCRIPTION,
  },
  {
    label: "Reports",
    value: IDocumentTypeEnum.REPORT,
  },
];

export const orderFilterList = [
  {
    label: "All",
    value: IOrderTypeEnums.ALL,
  },
  {
    label: "Clinic Visit",
    value: IOrderTypeEnums.IN_CLINIC,
  },
  {
    label: "Video Call",
    value: IOrderTypeEnums.VIDEO_CONSULTATION,
  },
  {
    label: "Medicine",
    value: IOrderTypeEnums.MEDICINE,
  },
  {
    label: "Tests",
    value: IOrderTypeEnums.TESTS,
  },
  {
    label: "Admit",
    value: IOrderTypeEnums.ADMIT,
  },
  {
    label: "Discharged",
    value: IOrderTypeEnums.DISCHARGED,
  },
];

export const faqAllTag = [
  {
    id: "all",
    tag: "All",
  },
];

export const allFilterList = [
  {
    label: "All",
    value: "all",
  },
];

export const metriceLabelByStatus: IKeyPairsValues = {
  [IProfileAnalyticsEnum.TOTAL_SALES]: "Total Sales",
  [IOrderStatusEnum.PENDING]: "Order Request",
  [IOrderStatusEnum.CONFIRMED]: "Order Accepted",
  [IOrderStatusEnum.REJECTED]: "Order Rejected",
  [IOrderStatusEnum.COMPLETED]: "Order Completed",
  [IOrderStatusEnum.CANCELLED]: "Order Cancelled",
};

export const statsData = [
  { name: "Specialty", number: 10 },
  { name: "Brain and Spine Surgeries", number: 10000 },
  { name: "Patients Life Touched", number: 60000 },
];

export const typeOfConsultation = [
  {
    label: IDoctorConsultationSelection.IN_CLINIC,
    value: "1",
  },
  {
    label: IDoctorConsultationSelection.VIDEO_CONSULTATION,
    value: "2",
  },
];

export const consultationTypes: IKeyPairsValues = {
  "1": IDoctorConsultationSelection.IN_CLINIC,
  "2": IDoctorConsultationSelection.VIDEO_CONSULTATION,
};

export const availabilityInputsNames: IKeyPairsValues = {
  startTime: "startTime",
  endTime: "endTime",
  slotDuration: "slotDuration",
  bufferDuration: "bufferDuration",
  consultationFee: "consultationFee",
  appointmentDisplay: "appointmentDisplay",
  totalSlots: "totalSlots",
  reserveSlots: "reserveSlots",
};

const logoutDropdownItems = [
  {
    iconName: "LogoutIcon",
    text: "logout",
    identifier: IProfileDropdownEnums.LOGOUT,
  },
];

const patientProfileMenuItems = [
  {
    iconName: "CartOrdersIcon",
    text: "your_orders",
    identifier: IProfileDropdownEnums.YOUR_ORDERS,
  },
  {
    iconName: "ProfileChatIcon",
    text: "chat",
    identifier: IProfileDropdownEnums.CHAT,
  },
  {
    iconName: "MemeberGroupIcon",
    text: "profiles",
    identifier: IProfileDropdownEnums.MEMBER_PROFILE,
  },
];
export const profileDropdownItems = [
  {
    iconName: "SmileSmallIcon",
    text: "profile",
    identifier: IProfileDropdownEnums.PROFILE,
  },
];

const helpSupportDropdownItem = [
  {
    iconName: "HelpSupportIcon",
    text: "help_support",
    identifier: IProfileDropdownEnums.HELP_SUPPORT,
  },
];

const doctorProfileDropdownItems = [
  ...profileDropdownItems,
  {
    iconName: "SettingSmallIcon",
    text: "availability",
    identifier: IProfileDropdownEnums.AVAILABILITY_SETTINGS,
  },
  {
    iconName: "StarSmallIcon",
    text: "reviews",
    identifier: IProfileDropdownEnums.REVIEW,
  },
  {
    iconName: "ClinicTeamSmallIcon",
    text: "clinical_team",
    identifier: IProfileDropdownEnums.CLINICAL_TEAM,
  },
  {
    iconName: "TemplatesSmallIcon",
    text: "templates",
    identifier: IProfileDropdownEnums.TEMPLATES,
  },
  ...helpSupportDropdownItem,
  {
    iconName: "ReportsSmallIcon",
    text: "reports",
    identifier: IProfileDropdownEnums.REPORTS,
  },
  ...logoutDropdownItems,
];

const pharmacyDiagnosticDropdownItems = [
  ...profileDropdownItems,
  ...logoutDropdownItems,
];

export const doctorPharmacyDiagnosticDropdownNavItems: any = {
  [IRoleType.DOCTOR]: doctorProfileDropdownItems,
  [IRoleType.JR_DOCTOR]: doctorProfileDropdownItems,
  [IRoleType.PHARMACY]: pharmacyDiagnosticDropdownItems,
  [IRoleType.DIAGNOSTIC_CENTER]: pharmacyDiagnosticDropdownItems,
};

export const patientDropdownNavItem = [
  ...patientProfileMenuItems,
  ...logoutDropdownItems,
];

export const patientTopNavItems = [
  {
    path: "/home",
    label: "home",
  },
  {
    path: "/find-doctor",
    label: "doctors",
  },
  {
    path: "/medicines",
    label: "medicines",
  },
  {
    path: "/test-reports",
    label: "tests",
  },
  {
    path: "/health-hub",
    label: "health",
  },
];

const doctorBottomNavigation = [
  {
    path: "/schedule",
    icon: "ScheduleIcon",
    selectedIcon: "ScheduleSelectedIcon",
    label: "schedule",
  },
  {
    path: "/ip-dash",
    icon: "IpDashIcon",
    selectedIcon: "IpDashSelectedIcon",
    label: "ip_dash",
  },
  {
    path: "/chats",
    icon: "ChatIcon",
    selectedIcon: "ChatSelectedIcon",
    label: "chat",
  },
  {
    path: "/profile",
    icon: "ProfileIcon",
    selectedIcon: "ProfileSelectedIcon",
    label: "profile",
  },
];

const pharmacyBottomNavigation = [
  {
    path: "/orders",
    icon: "OrderSmileIcon",
    selectedIcon: "OrderSmileSelectedIcon",
    label: "orders",
  },
  {
    path: "/clinic-patient",
    icon: "MyPatientsIcon",
    selectedIcon: "MyPatientsSelectedIcon",
    label: "my_patients",
  },
  {
    path: "/profile-details",
    icon: "PharmacyProfileSmileIcon",
    selectedIcon: "PharmacyProfileSmileSelectedIcon",
    label: "profile",
  },
];

const patientBottomNavigation = [
  {
    path: "/home",
    icon: "HomeIcon",
    selectedIcon: "HomeSelectedIcon",
    label: "home",
  },
  {
    path: "/find-doctor",
    icon: "DoctorIcon",
    selectedIcon: "DoctorSelectedIcon",
    label: "doctors",
  },
  {
    path: "/medicines",
    icon: "MedicineIcon",
    selectedIcon: "MedicineSelectedIcon",
    label: "medicines",
  },
  {
    path: "/test-reports",
    icon: "TestIcon",
    selectedIcon: "TestSelectedIcon",
    label: "tests",
  },
  {
    path: "/health-hub",
    icon: "TvIcon",
    selectedIcon: "TvSelectedIcon",
    label: "health",
  },
];
export const bottomNavigation = {
  [IRoleType.PATIENT]: patientBottomNavigation,
  [IRoleType.GUEST]: patientBottomNavigation,
  [IRoleType.DOCTOR]: doctorBottomNavigation,
  [IRoleType.JR_DOCTOR]: doctorBottomNavigation,
  [IRoleType.PHARMACY]: pharmacyBottomNavigation,
};

export const patientFooterData = {
  first: [
    {
      title: "global_neuro_and_spine_care",
      items: [
        "about_us",
        "our_app_features",
        "partner_with_us",
        "careers",
        "blog_and_resources",
        "contact_us",
        "chat_with_us",
        "faqs",
        "terms_and_conditions",
        "privacyPolicyText",
      ],
    },
  ],
  second: [
    {
      title: "our_app_for_doctors",
      items: [
        "grow_your_practice_online",
        "video_consultation_tools",
        "manage_appointments_and_ip_patients",
        "access_medical_records_securely",
      ],
    },
    {
      title: "partner_with_us",
      items: ["doctor_diagnostic_clinics_pharmacies"],
    },
    {
      title: "international_patients",
      items: ["request_an_appointment", "get_second_opinion"],
    },
  ],
  third: [
    {
      title: "for_patients",
      items: [
        "book_appointment",
        "get_second_opinion",
        "online_doctor_consultation",
        "free_MRI_CT_review",
        "book_lab_tests",
        "order_medicines",
        "neuro_health_tips",
        "neuro_health_assessment_tool",
      ],
    },
  ],
  popularSearches: [
    "Best Neurosurgeon Near me",
    "Top Neurologist in Durgapur",
    "Spine Specialist in West Bengal",
    "Brain Doctor in Durgapur",
    "Best Brain Tumor Doctor in India",
    "Brain Tumor Specialist in Kolkata",
    "Back Pain Specialist Near Me",
    "Sciatica Treatment in Durgapur",
    "Lumbar Spondylosis Doctor Near Me",
    "Top Neurosurgeon in Durgapur",
    "Top Neurosurgeon in West Bengal",
    "Top Neuro doctor in India",
  ],
  socialIconsList: socialLinks,
};

const doctorOrStaffDesktopTopNav = [
  {
    path: "/schedule",
    icon: <GetImages name="ScheduleLargeIcon" width="32" height="32" />,
    selectedIcon: (
      <GetImages name="ScheduleSelectedLargeIcon" width="32" height="32" />
    ),
    label: "schedule",
  },
  {
    path: "/ip-dash",
    icon: <GetImages name="IpDashLargeIcon" width="32" height="32" />,
    selectedIcon: (
      <GetImages name="IpDashLargeSelectedIcon" width="32" height="32" />
    ),
    label: "ip_dashboard",
  },
  {
    path: "/opd-reception",
    icon: <GetImages name="ReceptionIcon" width="32" height="32" />,
    selectedIcon: (
      <GetImages name="ReceptionSelectedIcon" width="32" height="32" />
    ),
    label: "reception",
  },
];
export const doctorPharmacyDiagnosticDesktopTopNav = () => ({
  [IRoleType.DOCTOR]: doctorOrStaffDesktopTopNav,
  [IRoleType.JR_DOCTOR]: doctorOrStaffDesktopTopNav,
  [IRoleType.PHARMACY]: [
    ...(isClinic()
      ? [
          {
            path: "/clinic-patient",
            icon: <GetImages name="MyPatientIcon" width="36" height="36" />,
            selectedIcon: (
              <GetImages name="MyPatientSelectedIcon" width="36" height="36" />
            ),
            label: "my_patients",
          },
        ]
      : []),
    {
      path: "/orders",
      icon: <GetImages name="PharmacyOrdersIcon" width="36" height="36" />,
      selectedIcon: (
        <GetImages name="PharmacyOrdersSelectedIcon" width="36" height="36" />
      ),
      label: "orders",
    },
  ],
  [IRoleType.DIAGNOSTIC_CENTER]: [
    {
      path: "/orders",
      icon: <GetImages name="ScheduleLargeIcon" width="32" height="32" />,
      selectedIcon: (
        <GetImages name="ScheduleSelectedLargeIcon" width="32" height="32" />
      ),
      label: "orders",
    },
  ],
});

export const doctorSpecialitiesBreakpoints = {
  0: {
    slidesPerView: 1.9,
    spaceBetween: 10,
  },
  450: {
    slidesPerView: 2,
    spaceBetween: 20,
  },
  768: {
    slidesPerView: 3.5,
    spaceBetween: 40,
  },
  900: {
    slidesPerView: 5,
    spaceBetween: 30,
  },
  1400: {
    slidesPerView: 6,
    spaceBetween: 30,
  },
  1600: {
    slidesPerView: 8,
    spaceBetween: 50,
  },
};
export enum IShareButtonIcon {
  GMAIL = "RecordsGmailIcon",
  WHATSAPP = "RecordsWhatsappIcon",
  PRINT = "RecordsPrintIcon",
}

export interface IShareButton {
  icon: IShareButtonIcon;
  onClick: (url?: string, title?: string) => void;
}

const handleGmailShare = (url: string, title: string) => {
  const body = `Please find link: ${url}`;
  const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&su=${encodeURIComponent(
    title
  )}&body=${encodeURIComponent(body)}`;
  window.open(gmailUrl, "_blank");
};

const handleWhatsAppShare = (url: string, title: string) => {
  const text = `${title}: ${url}`;
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
  window.open(whatsappUrl, "_blank");
};

export const shareButtons: IShareButton[] = [
  {
    icon: IShareButtonIcon.GMAIL,
    onClick: (pdfUrl: string | undefined, title: string | undefined) => {
      if (pdfUrl && title) handleGmailShare(pdfUrl, title);
    },
  },
  {
    icon: IShareButtonIcon.WHATSAPP,
    onClick: (pdfUrl: string | undefined, title: string | undefined) => {
      if (pdfUrl && title) handleWhatsAppShare(pdfUrl, title);
    },
  },
  {
    icon: IShareButtonIcon.PRINT,
    onClick: (pdfUrl: string | undefined) => {
      if (pdfUrl) downloadReport(pdfUrl, "prescription", false);
    },
  },
];

export const paymentMethodValues = [
  {
    label: IPaymentMethod.CASH,
    value: IPaymentMethod.CASH,
  },
  {
    label: IPaymentMethod.UPI,
    value: IPaymentMethod.UPI,
  },
];

// Keyframes for the shimmer effect
const shimmer = keyframes`
  0% {
    background-position: -200%;
  }
  100% {
    background-position: 200%;
  }
`;

// Styled Button with shimmer effect
export const ShimmerButton = styled(Button)(
  ({ disabled }: { disabled: boolean }) => ({
    width: "100%",
    padding: "1.2rem",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      background: `linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.3) 50%,
      rgba(255, 255, 255, 0) 100%
    )`,
      backgroundSize: "200% 100%",
      animation: `${shimmer} 1.5s infinite`,
      animationPlayState: disabled ? "paused" : "running",
    },
    "&:hover::before": {
      animationPlayState: "paused",
    },
  })
);

export function withCountUp(
  WrappedComponent: React.ComponentType,
  end: number,
  duration = 2000,
  start = 0
) {
  return function CountUpComponent(props) {
    const [count, setCount] = useState(start);

    useEffect(() => {
      let animationFrame;
      const startTime = performance.now();

      const step = (time) => {
        const progress = Math.min((time - startTime) / duration, 1);
        setCount(Math.floor(progress * (end - start) + start));
        if (progress < 1) requestAnimationFrame(step);
      };

      animationFrame = requestAnimationFrame(step);
      return () => cancelAnimationFrame(animationFrame);
    }, [end, duration, start]);

    return <WrappedComponent {...props} count={count} />;
  };
}

export const homeStepper = [
  {
    icon: "DoctorSearchStepperIcon",
    messageId: "homeStep1",
    defaultMessage:
      "Find the right specialist by searching based on your location, symptoms, or specialty",
    showLines: true,
  },
  {
    icon: "ScheduleAppointmentStepperIcon",
    messageId: "homeStep2",
    defaultMessage:
      "View the doctor’s availability and select a convenient date and time for your appointment",
    showLines: true,
  },
  {
    icon: "PrescriptionStepperIcon",
    messageId: "homeStep3",
    defaultMessage:
      "Secure your appointment instantly by completing a quick and easy payment process",
    showLines: true,
  },
  {
    icon: "ContactSupportStepperIcon",
    messageId: "homeStep4",
    defaultMessage:
      "Join your video consultation or visit the clinic at your scheduled time",
    showLines: false,
  },
];
